import { useEffect, useState } from 'react';
import { ChatAvatar, OWN_BUBBLE, PEER_BUBBLE } from '@reloam/ui';
import { HiArrowRight, HiCheckBadge, HiOutlineSparkles, HiSparkles } from 'react-icons/hi2';
import type { NavAction, NavTargetKey } from '../../services/assistant/navTargets';
import type { ReplySource } from '../../services/assistant/engine';
import AssistantReply from './AssistantReply';
import type { AssistantMessage } from './assistantStore';

export function AssistantAvatar() {
  return (
    <span className="mt-0.5 flex-shrink-0">
      <ChatAvatar icon={HiSparkles} size="sm" tone="accent" />
    </span>
  );
}

export const BUBBLE = PEER_BUBBLE;

const SOURCE_LABEL: Record<ReplySource, { text: string; title: string }> = {
  data: { text: 'From your records', title: 'Calculated directly from your plots, leases and invoices' },
  ai: { text: 'AI answer, check key figures', title: 'Written by the AI model using a summary of your records' },
  fallback: { text: 'Offline summary', title: 'The AI model did not respond, so this was built from your records' },
};

function SourceTag({ source }: { source: ReplySource }) {
  const label = SOURCE_LABEL[source];
  const Icon = source === 'ai' ? HiOutlineSparkles : HiCheckBadge;
  return (
    <span title={label.title} className="inline-flex items-center gap-1 text-[11px] text-bodydark1 dark:text-bodydark2">
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label.text}
    </span>
  );
}

interface ActionsProps {
  actions: NavAction[];
  onNavigate: (target: NavTargetKey) => void;
}

function Actions({ actions, onNavigate }: ActionsProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((a) => (
        <button
          key={a.target}
          type="button"
          onClick={() => onNavigate(a.target)}
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary-dark transition-colors hover:bg-primary/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-primary/40 dark:text-primary-lighter"
        >
          {a.label}
          <HiArrowRight className="h-3 w-3" aria-hidden />
        </button>
      ))}
    </div>
  );
}

interface MessageRowProps {
  message: AssistantMessage;
  onNavigate: (target: NavTargetKey) => void;
}

export function MessageRow({ message, onNavigate }: MessageRowProps) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className={`max-w-[85%] whitespace-pre-wrap break-words ${OWN_BUBBLE}`}>
          {message.content}
        </p>
      </div>
    );
  }
  return (
    <div className="flex gap-2.5">
      <AssistantAvatar />
      <div className="flex min-w-0 max-w-[88%] flex-col gap-2">
        <div className={BUBBLE}>
          <AssistantReply content={message.content} onNavigate={onNavigate} />
        </div>
        {message.actions && message.actions.length > 0 && <Actions actions={message.actions} onNavigate={onNavigate} />}
        {message.source && <SourceTag source={message.source} />}
      </div>
    </div>
  );
}

const SLOW_AFTER_MS = 5000;

export function TypingIndicator() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="flex gap-2.5" role="status" aria-live="polite">
      <AssistantAvatar />
      <div className={`${BUBBLE} flex items-center gap-2.5`}>
        <span className="flex gap-1" aria-hidden>
          <span className="h-2 w-2 animate-bounce rounded-full bg-primary/70 [animation-delay:-0.3s]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-primary/70 [animation-delay:-0.15s]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-primary/70" />
        </span>
        <span className="text-xs text-bodydark1 dark:text-bodydark2">
          {slow ? 'Still thinking, this can take a few seconds…' : 'Looking at your records…'}
        </span>
      </div>
    </div>
  );
}
